import { ORGANIZATION_TYPE_SUGGESTIONS } from '@lib/organizations/constants/organization-types';

export interface SetupOption {
  id: string;
  label: string;
  description?: string;
}

export type GoalId =
  | 'replace_legacy'
  | 'collect_esignatures'
  | 'sign_documents'
  | 'web_forms'
  | 'automate_contracts'
  | 'contract_insights'
  | 'create_contracts'
  | 'compliance'
  | 'payments'
  | 'conversation_to_close';

export interface GoalOption extends SetupOption {
  id: GoalId;
  description: string;
}

export const GOAL_OPTIONS: readonly GoalOption[] = [
  {
    id: 'replace_legacy',
    label: 'Replace legacy tools',
    description: 'Retire the patchwork of apps, drives and inboxes.',
  },
  {
    id: 'collect_esignatures',
    label: 'Collect eSignatures',
    description: 'Send documents out and track every signer.',
  },
  {
    id: 'sign_documents',
    label: 'Sign documents',
    description: 'Sign anything you receive in seconds.',
  },
  {
    id: 'web_forms',
    label: 'Build web forms',
    description: 'Shareable forms that turn answers into documents.',
  },
  {
    id: 'automate_contracts',
    label: 'Automate existing contracts',
    description: 'Turn your templates into one-click, auto-filled contracts.',
  },
  {
    id: 'contract_insights',
    label: 'Gain insight into past contracts',
    description: 'Chat with past docs and pull out dates, terms and totals.',
  },
  {
    id: 'create_contracts',
    label: 'Create contracts',
    description: 'Draft new contracts with Clai AI.',
  },
  {
    id: 'compliance',
    label: 'Manage compliance',
    description: 'Checklists and audit trails on every deal.',
  },
  {
    id: 'payments',
    label: 'Collect payments on contracts',
    description: 'Get paid the moment the ink dries.',
  },
  {
    id: 'conversation_to_close',
    label: 'Go from conversation to close!',
    description: 'One flow from first chat to signed and paid.',
  },
];

/**
 * The most common firm types from the Coach Tour list, shown as quick picks.
 * Anything else is still reachable by typing (see ALL_COMPANY_TYPES).
 */
export const FEATURED_COMPANY_TYPES: readonly string[] = [
  'Residential real estate',
  'Real estate brokerage',
  'Commercial real estate',
  'Property management',
  'Real estate investor',
  'Real estate developer',
  'Title / escrow',
  'Mortgage / lending',
  'Law firm',
  'Accounting / tax',
  'Consulting firm',
  'Construction / general contracting',
  'Insurance agency',
];

export const ALL_COMPANY_TYPES: readonly string[] = Array.from(
  new Set([...FEATURED_COMPANY_TYPES, ...ORGANIZATION_TYPE_SUGGESTIONS])
);

export const EMPLOYEE_RANGE_OPTIONS: readonly SetupOption[] = [
  { id: 'just_me', label: 'Just me' },
  { id: '2-10', label: '2–10' },
  { id: '11-50', label: '11–50' },
  { id: '51-200', label: '51–200' },
  { id: '201-500', label: '201–500' },
  { id: '500+', label: '500+' },
];

export const CURRENT_TOOL_OPTIONS: readonly SetupOption[] = [
  { id: 'docusign', label: 'DocuSign' },
  { id: 'dotloop', label: 'Dotloop' },
  { id: 'skyslope', label: 'SkySlope' },
  { id: 'adobe_sign', label: 'Adobe Acrobat Sign' },
  { id: 'pandadoc', label: 'PandaDoc' },
  { id: 'docs_word', label: 'Google Docs / Word' },
  { id: 'spreadsheets', label: 'Spreadsheets' },
  { id: 'paper', label: 'Paper & scanning' },
  { id: 'other', label: 'Something else' },
];

/** Real estate transaction tools, offered only to real estate industries. */
export const REAL_ESTATE_TOOL_IDS: readonly string[] = ['dotloop', 'skyslope'];

export const MONTHLY_VOLUME_OPTIONS: readonly SetupOption[] = [
  { id: '1-10', label: '1–10 a month' },
  { id: '11-50', label: '11–50 a month' },
  { id: '51-200', label: '51–200 a month' },
  { id: '200+', label: '200+ a month' },
  { id: 'not_sure', label: 'Not sure yet' },
];

export const REFERRAL_OPTIONS: readonly SetupOption[] = [
  { id: 'search', label: 'Google or another search engine' },
  { id: 'ai_assistant', label: 'An AI assistant (ChatGPT, Claude…)' },
  { id: 'social', label: 'Social media' },
  { id: 'colleague', label: 'A friend or colleague' },
  { id: 'event', label: 'An event or podcast' },
  { id: 'clai_team', label: 'Someone from Clai reached out' },
  { id: 'other', label: 'Somewhere else' },
];

/** Forest green: the first swatch, used until the user picks a color. */
export const DEFAULT_BRAND_COLOR = '#166534';

export const BRAND_COLOR_SWATCHES: readonly string[] = [
  DEFAULT_BRAND_COLOR,
  '#22c55e',
  '#0ea5e9',
  '#2563eb',
  '#4f46e5',
  '#7c3aed',
  '#db2777',
  '#e11d48',
  '#ea580c',
  '#d97706',
  '#0d9488',
  '#334155',
  '#111827',
];

export function labelFor(
  options: readonly SetupOption[],
  id: string | null | undefined
): string {
  return options.find(o => o.id === id)?.label ?? '';
}
